/**
 * localStorage persistence helpers for the alert-notifications
 * provider. Split out from the provider component file so React
 * Fast Refresh can hot-reload the provider without re-running the
 * persistence constants.
 *
 * Only READ state is persisted. Versions ≤2 also stored an
 * `archived` array of full alert payloads — a localStorage-only
 * archive that hid rows in the bell while the same rows stayed open
 * on the Alerts tab and every other operator's screen. That feature
 * is gone (server-side resolve + per-device mutes replaced it), so
 * v3 drops the field. The reader still accepts the old payloads —
 * it lifts their `read` array and silently ignores `archived`, and
 * the next save rewrites the key without it.
 *
 * Public API used by the provider: `loadPersistedState`,
 * `saveReadKey`, plus the test-only `_saveReadForTest` /
 * `_resetStorageState` / `_getStorageState` helpers.
 */

export const STORAGE_PREFIX = 'nx:alerts:state';
export const STORAGE_VERSION = 3;

export function stateKey(userId: string, siteId: string): string {
  return `${STORAGE_PREFIX}:${userId}:${siteId}`;
}

export interface PersistedState {
  v: number;
  read: string[];
}

export const EMPTY_STATE: PersistedState = {
  v: STORAGE_VERSION,
  read: [],
};

/** Probe localStorage with a tiny throwaway write. Returns false
 *  in Safari Private Mode + sandboxed iframes where every setItem
 *  throws. */
export function probeStorageAvailable(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const probeKey = `${STORAGE_PREFIX}:probe`;
    window.localStorage.setItem(probeKey, '1');
    window.localStorage.removeItem(probeKey);
    return true;
  } catch {
    return false;
  }
}

/** Parse + migrate a persisted payload. Returns the current-shape
 *  state. Any version ≤ `STORAGE_VERSION` (including the pre-`v`
 *  legacy shape) is accepted: every historical shape carried `read`
 *  as a string array, and that is the only field that survives.
 *  Payloads that additionally carry the retired `archived` data
 *  (v≤2) load without error — the field is simply not read. On
 *  parse failure or a FUTURE version we fall back to an empty state
 *  (and log, for the future case) rather than guessing at a shape
 *  written by newer code. */
export function loadPersistedState(key: string): PersistedState {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(key);
  } catch {
    return EMPTY_STATE;
  }
  if (!raw) return EMPTY_STATE;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return EMPTY_STATE;
  }
  if (!parsed || typeof parsed !== 'object') return EMPTY_STATE;
  const o = parsed as Record<string, unknown>;
  const v = typeof o.v === 'number' ? o.v : 1;

  // Future version: log and reset rather than silently rendering
  // broken rows.
  if (v > STORAGE_VERSION) {
    console.warn(
      `[alert-notifications] persisted state version ${v} is newer than supported ${STORAGE_VERSION}; ignoring.`,
    );
    return EMPTY_STATE;
  }

  const read = Array.isArray(o.read)
    ? (o.read as unknown[]).filter((x): x is string => typeof x === 'string')
    : [];

  return { v: STORAGE_VERSION, read };
}

/** Module-level latches consumed by the provider to detect
 *  persistent localStorage unavailability (Safari Private, sandboxed
 *  iframes). Tests reset these via `_resetStorageState()`. */
let storageWriteFailed = false;
let storageWriteAttempts = 0;

export function _resetStorageState(): void {
  storageWriteFailed = false;
  storageWriteAttempts = 0;
}
export function _getStorageState(): { failed: boolean; attempts: number } {
  return { failed: storageWriteFailed, attempts: storageWriteAttempts };
}

/** Persist `{v, read}`. Deliberately REPLACES the whole key: this is
 *  also what scrubs the retired `archived` payload out of storage the
 *  first time a migrated browser writes anything. */
export function saveReadKey(key: string, v: number, read: string[]): void {
  try {
    storageWriteAttempts += 1;
    window.localStorage.setItem(key, JSON.stringify({ v, read }));
    storageWriteFailed = false;
  } catch {
    storageWriteAttempts += 1;
    storageWriteFailed = true;
  }
}

/** Test helper: write the read half through the production path. */
export function _saveReadForTest(key: string, read: string[]): void {
  saveReadKey(key, STORAGE_VERSION, read);
}
